/**
 * Motion as data: animation groups, and the arithmetic that turns one into values at a moment.
 *
 * Shaped after World of Warcraft's `<AnimationGroup>`, which has carried a decade of player-made
 * UI: a group is a list of steps, each moving one channel from one value to another, and steps
 * with a higher `order` wait for every step of the order before them. Everything here is pure -
 * "where is this group at 0.35s" is a function call, not a screenshot - and `MotionList` is the
 * only part that knows about clocks or controls.
 */

/** What a step can move. Each composes across groups in the way its name suggests. */
export type MotionChannel = "alpha" | "x" | "y" | "scale" | "reveal";

export const MOTION_CHANNELS: readonly MotionChannel[] = ["alpha", "x", "y", "scale", "reveal"];

/**
 * What starts a group.
 *
 * `load` and `reveal` play once: the first frame the control is painted at all, and the first
 * frame its resting place is on screen. `hover`, `press` and `focus` are held states - the group
 * runs forward while the state holds and back when it lets go. `manual` waits for `play()`.
 */
export type MotionTrigger = "load" | "reveal" | "hover" | "press" | "focus" | "manual";

export const MOTION_TRIGGERS: readonly MotionTrigger[] = [
  "load",
  "reveal",
  "hover",
  "press",
  "focus",
  "manual",
];

/** `repeat` starts over; `bounce` runs back and forth. Ignored for held triggers. */
export type MotionLooping = "none" | "repeat" | "bounce";

export const MOTION_LOOPING: readonly MotionLooping[] = ["none", "repeat", "bounce"];

export type MotionCurve = "linear" | "smooth" | "in" | "out" | "inOut" | "back";

export const MOTION_CURVES: readonly MotionCurve[] = ["linear", "smooth", "in", "out", "inOut", "back"];

export interface MotionStep {
  channel: MotionChannel;
  from: number;
  to: number;
  /** Seconds. Zero is a jump. */
  duration: number;
  /** Seconds after this step's stage begins. */
  delay?: number;
  /** Steps of one order run together; the next order waits for all of them. */
  order?: number;
  curve?: MotionCurve;
}

export interface AnimationGroupSpec {
  /** How `play`, `stop` and the finished event refer to it. */
  name?: string;
  trigger: MotionTrigger;
  looping?: MotionLooping;
  steps: MotionStep[];
}

/** One frame's worth of motion for a control: where it is drawn, how large, how opaque. */
export interface MotionValues {
  alpha: number;
  x: number;
  y: number;
  scale: number;
  /** How much of itself a control that can draw partially should draw, 0..1. */
  reveal: number;
}

export const REST: Readonly<MotionValues> = { alpha: 1, x: 0, y: 0, scale: 1, reveal: 1 };

export function restValues(): MotionValues {
  return { ...REST };
}

/** Shape a 0..1 progress. Every curve starts at 0 and ends at 1. */
export function ease(curve: MotionCurve | undefined, p: number): number {
  const t = p <= 0 ? 0 : p >= 1 ? 1 : p;
  switch (curve ?? "smooth") {
    case "linear":
      return t;
    case "in":
      return t * t * t;
    case "out":
      return 1 - (1 - t) ** 3;
    case "inOut":
      return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
    case "back": {
      // Overshoots by about ten per cent and settles: the one curve that reads as "arrived".
      const c = 1.70158;
      return 1 + (c + 1) * (t - 1) ** 3 + c * (t - 1) ** 2;
    }
    default:
      return t * t * (3 - 2 * t);
  }
}

interface Stage {
  start: number;
  length: number;
}

/** When each order begins, and how long the whole group runs. */
export function groupTimeline(spec: AnimationGroupSpec): { stages: Map<number, Stage>; total: number } {
  const lengths = new Map<number, number>();
  for (const step of spec.steps) {
    const order = step.order ?? 0;
    const end = Math.max(0, step.delay ?? 0) + Math.max(0, step.duration);
    lengths.set(order, Math.max(lengths.get(order) ?? 0, end));
  }
  const stages = new Map<number, Stage>();
  let at = 0;
  for (const order of [...lengths.keys()].sort((a, b) => a - b)) {
    const length = lengths.get(order)!;
    stages.set(order, { start: at, length });
    at += length;
  }
  return { stages, total: at };
}

export function groupDuration(spec: AnimationGroupSpec): number {
  return groupTimeline(spec).total;
}

/**
 * Where on its own timeline a group is after `elapsed` seconds of playing.
 *
 * A group that does not loop holds its last frame; that is what makes an entrance leave the
 * control where it belongs rather than snapping back to where it started.
 */
export function localTime(spec: AnimationGroupSpec, elapsed: number): number {
  const total = groupDuration(spec);
  if (total <= 0) return 0;
  const t = Math.max(0, elapsed);
  switch (spec.looping ?? "none") {
    case "repeat":
      return t % total;
    case "bounce": {
      const cycle = t % (total * 2);
      return cycle <= total ? cycle : total * 2 - cycle;
    }
    default:
      return Math.min(t, total);
  }
}

/**
 * The value of each channel the group moves, at time `t` on its own timeline.
 *
 * Within a group a channel belongs to the latest step that has begun, so a fade in followed by a
 * fade out is two steps and reads the way it is written. Before its first step begins a channel
 * holds that step's `from` - an entrance waiting on its delay is already invisible, not visible
 * for a moment and then gone. A channel the group never mentions is absent from the result.
 */
export function evaluateGroup(spec: AnimationGroupSpec, t: number): Partial<MotionValues> {
  const { stages } = groupTimeline(spec);
  // Per channel: the latest step that has begun, or failing that the earliest one there is.
  const chosen = new Map<MotionChannel, { step: MotionStep; begins: number }>();
  for (const step of spec.steps) {
    const begins = stages.get(step.order ?? 0)!.start + Math.max(0, step.delay ?? 0);
    const current = chosen.get(step.channel);
    const better =
      !current ||
      (begins <= t ? current.begins > t || begins >= current.begins : current.begins > t && begins < current.begins);
    if (better) chosen.set(step.channel, { step, begins });
  }
  const out: Partial<MotionValues> = {};
  for (const [channel, { step, begins }] of chosen) {
    const p = step.duration <= 0 ? (t >= begins ? 1 : 0) : (t - begins) / step.duration;
    out[channel] = step.from + (step.to - step.from) * ease(step.curve, p);
  }
  return out;
}

/**
 * Fold one group's values into a running total.
 *
 * Fades and scales multiply and offsets add, so a control rising in while it breathes does both.
 * Reveal multiplies too: two groups each half-drawing a control leave it a quarter drawn, which
 * is what an author stacking them would expect to see.
 */
export function compose(into: MotionValues, part: Partial<MotionValues>): MotionValues {
  if (part.alpha !== undefined) into.alpha *= part.alpha;
  if (part.x !== undefined) into.x += part.x;
  if (part.y !== undefined) into.y += part.y;
  if (part.scale !== undefined) into.scale *= part.scale;
  if (part.reveal !== undefined) into.reveal *= part.reveal;
  return into;
}

// --- presets --------------------------------------------------------------

export interface PresetOptions {
  /** Seconds; each preset has its own default. */
  duration?: number;
  delay?: number;
  /** Pixels for the moving presets, a fraction for the scaling and fading ones. */
  amount?: number;
  name?: string;
}

type PresetBuilder = (o: Required<Omit<PresetOptions, "name">>) => Omit<AnimationGroupSpec, "name">;

interface PresetDefinition {
  kind: "entrance" | "loop" | "hover" | "press";
  duration: number;
  amount: number;
  build: PresetBuilder;
}

const PRESETS: Record<string, PresetDefinition> = {
  // Entrances: played once, when the control is first shown (or first scrolled to).
  fade: {
    kind: "entrance",
    duration: 0.6,
    amount: 0,
    build: (o) => ({
      trigger: "load",
      steps: [{ channel: "alpha", from: 0, to: 1, duration: o.duration, delay: o.delay }],
    }),
  },
  rise: {
    kind: "entrance",
    duration: 0.7,
    amount: 16,
    build: (o) => ({
      trigger: "load",
      steps: [
        { channel: "alpha", from: 0, to: 1, duration: o.duration, delay: o.delay },
        { channel: "y", from: o.amount, to: 0, duration: o.duration, delay: o.delay, curve: "out" },
      ],
    }),
  },
  drop: {
    kind: "entrance",
    duration: 0.7,
    amount: 16,
    build: (o) => ({
      trigger: "load",
      steps: [
        { channel: "alpha", from: 0, to: 1, duration: o.duration, delay: o.delay },
        { channel: "y", from: -o.amount, to: 0, duration: o.duration, delay: o.delay, curve: "out" },
      ],
    }),
  },
  slideLeft: {
    kind: "entrance",
    duration: 0.7,
    amount: 32,
    build: (o) => ({
      trigger: "load",
      steps: [
        { channel: "alpha", from: 0, to: 1, duration: o.duration, delay: o.delay },
        { channel: "x", from: o.amount, to: 0, duration: o.duration, delay: o.delay, curve: "out" },
      ],
    }),
  },
  slideRight: {
    kind: "entrance",
    duration: 0.7,
    amount: 32,
    build: (o) => ({
      trigger: "load",
      steps: [
        { channel: "alpha", from: 0, to: 1, duration: o.duration, delay: o.delay },
        { channel: "x", from: -o.amount, to: 0, duration: o.duration, delay: o.delay, curve: "out" },
      ],
    }),
  },
  grow: {
    kind: "entrance",
    duration: 0.6,
    amount: 0.85,
    build: (o) => ({
      trigger: "load",
      steps: [
        { channel: "alpha", from: 0, to: 1, duration: o.duration, delay: o.delay },
        { channel: "scale", from: o.amount, to: 1, duration: o.duration, delay: o.delay, curve: "back" },
      ],
    }),
  },
  drawIn: {
    kind: "entrance",
    duration: 1.4,
    amount: 0,
    build: (o) => ({
      trigger: "load",
      steps: [{ channel: "reveal", from: 0, to: 1, duration: o.duration, delay: o.delay }],
    }),
  },

  // Loops: run for as long as the control is on screen.
  breathe: {
    kind: "loop",
    duration: 2.4,
    amount: 0.7,
    build: (o) => ({
      trigger: "load",
      looping: "bounce",
      steps: [{ channel: "alpha", from: 1, to: o.amount, duration: o.duration, delay: o.delay }],
    }),
  },
  pulse: {
    kind: "loop",
    duration: 1.2,
    amount: 1.04,
    build: (o) => ({
      trigger: "load",
      looping: "bounce",
      steps: [{ channel: "scale", from: 1, to: o.amount, duration: o.duration, delay: o.delay }],
    }),
  },
  float: {
    kind: "loop",
    duration: 2.8,
    amount: 6,
    build: (o) => ({
      trigger: "load",
      looping: "bounce",
      steps: [{ channel: "y", from: 0, to: -o.amount, duration: o.duration, delay: o.delay }],
    }),
  },
  sway: {
    kind: "loop",
    duration: 3.2,
    amount: 4,
    build: (o) => ({
      trigger: "load",
      looping: "bounce",
      steps: [
        { channel: "x", from: -o.amount, to: o.amount, duration: o.duration, delay: o.delay },
      ],
    }),
  },
  twinkle: {
    kind: "loop",
    duration: 0.9,
    amount: 0.35,
    build: (o) => ({
      trigger: "load",
      looping: "bounce",
      steps: [{ channel: "alpha", from: 1, to: o.amount, duration: o.duration, delay: o.delay }],
    }),
  },

  // Reactions: held while the pointer is over the control, or pressing it.
  lift: {
    kind: "hover",
    duration: 0.18,
    amount: 3,
    build: (o) => ({
      trigger: "hover",
      steps: [{ channel: "y", from: 0, to: -o.amount, duration: o.duration, curve: "out" }],
    }),
  },
  enlarge: {
    kind: "hover",
    duration: 0.18,
    amount: 1.04,
    build: (o) => ({
      trigger: "hover",
      steps: [{ channel: "scale", from: 1, to: o.amount, duration: o.duration, curve: "out" }],
    }),
  },
  brighten: {
    kind: "hover",
    duration: 0.18,
    amount: 0.8,
    build: (o) => ({
      trigger: "hover",
      steps: [{ channel: "alpha", from: o.amount, to: 1, duration: o.duration }],
    }),
  },
  sink: {
    kind: "press",
    duration: 0.08,
    amount: 0.97,
    build: (o) => ({
      trigger: "press",
      steps: [{ channel: "scale", from: 1, to: o.amount, duration: o.duration, curve: "out" }],
    }),
  },
};

export type PresetKind = PresetDefinition["kind"];

/** The preset names of one kind, in the order they are listed here - for a picker. */
export function motionPresets(kind?: PresetKind): string[] {
  return Object.entries(PRESETS)
    .filter(([, d]) => kind === undefined || d.kind === kind)
    .map(([name]) => name);
}

export function presetKind(name: string): PresetKind | null {
  return PRESETS[name]?.kind ?? null;
}

/**
 * A named preset, expanded into the group it stands for.
 *
 * Presets are the one-click layer over groups, not a second system: what comes back is an
 * ordinary `AnimationGroupSpec`, so anything a preset does a hand-written group can do too, and a
 * designer can offer "start from rise" and let the author edit the steps.
 */
export function motionPreset(name: string, options: PresetOptions = {}): AnimationGroupSpec {
  const definition = PRESETS[name];
  if (!definition) {
    throw new Error(
      `webwin: unknown motion preset "${name}". Presets: ${Object.keys(PRESETS).join(", ")}`,
    );
  }
  const group = definition.build({
    duration: finite(options.duration, definition.duration),
    delay: Math.max(0, finite(options.delay, 0)),
    amount: finite(options.amount, definition.amount),
  });
  return { ...group, name: options.name ?? name };
}

function finite(value: number | undefined, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}
